const inflight = new Map<string, Promise<string>>();

export class EmailRegisterError extends Error {
  code: string;

  constructor(message: string, code = '') {
    super(message);
    this.name = 'EmailRegisterError';
    this.code = code;
  }
}

/** S1 public base. Empty config stays on the production account gate. */
export function accountApiBase(configured?: string): string {
  const value = String(configured ?? '').trim();
  return (value || 'https://proxy.cyberdash.net').replace(/\/+$/, '');
}

/**
 * One in-flight registration per email in this page. A second submit shares
 * the same custom token instead of opening a second Firebase user.
 */
export function requestEmailRegister(
  apiBase: string,
  email: string,
  password: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const normalized = String(email || '').trim().toLowerCase();
  const secret = String(password || '');
  if (!normalized.includes('@') || normalized.length > 320) {
    return Promise.reject(new EmailRegisterError('Invalid email', 'auth/invalid-email'));
  }
  if (secret.length < 6) {
    return Promise.reject(new EmailRegisterError('Password must be at least 6 characters', 'auth/weak-password'));
  }

  const key = `${apiBase}\n${normalized}`;
  const pending = inflight.get(key);
  if (pending) return pending;

  const run = registerOnce(apiBase, normalized, secret, fetchImpl).finally(() => {
    if (inflight.get(key) === run) inflight.delete(key);
  });
  inflight.set(key, run);
  return run;
}

/** S1 answers 401 for a wrong password on an email it knows, and states nothing more. */
function registerErrorCode(status: number, message: string): string {
  if (status === 401) return 'auth/invalid-credential';
  return status === 400 && /at least/i.test(message) ? 'auth/weak-password' : '';
}

async function registerOnce(
  apiBase: string,
  email: string,
  password: string,
  fetchImpl: typeof fetch,
): Promise<string> {
  const response = await fetchImpl(`${accountApiBase(apiBase)}/api/v1/auth/email/register`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = await response.json().catch(() => ({})) as { customToken?: string; message?: string };
  if (!response.ok || !body.customToken) {
    const message = typeof body.message === 'string' && body.message
      ? body.message
      : 'Could not create the account or sign in.';
    throw new EmailRegisterError(message, registerErrorCode(response.status, message));
  }
  return body.customToken;
}
